import React from 'react'
import {
  Box,
  Grid,
  Typography,
  Divider,
  Alert,
  Chip,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import ContactMailIcon from '@mui/icons-material/ContactMail'
import TuneIcon from '@mui/icons-material/Tune'
import ShareIcon from '@mui/icons-material/Share'
import InfoIcon from '@mui/icons-material/Info'

import ConfigSection from '../components/ConfigSection'
import ConfigField from '../components/ConfigField'
import ConfigSelect from '../components/ConfigSelect'
import ConfigToggle from '../components/ConfigToggle'
import { PlatformConfig } from '../types/EnvironmentConfig.types'
import { useWorkspaceBranding } from '../hooks/useWorkspaceBranding'
import {
  environmentConfigSectionDividerSx,
  environmentConfigSectionRootSx,
} from '../utils/environmentConfigChrome'

interface PlatformSettingsProps {
  config: PlatformConfig
  onChange: (updates: Partial<PlatformConfig>) => void
  disabled?: boolean
}

/**
 * Platform Settings Section
 * Manages contact details and social media links
 */
const PlatformSettings: React.FC<PlatformSettingsProps> = ({
  config,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme()
  const { branding, loading: brandingLoading } = useWorkspaceBranding()

  const handleChange = (field: keyof PlatformConfig, value: any) => {
    onChange({ [field]: value })
  }

  /**
   * One application-wide feature switch. Flipping it takes effect for everyone on their next load, with no
   * deploy — which is the whole reason SC-209's switch lives here rather than as a build-time constant.
   */
  const handleFeatureChange = (feature: 'pictureInPicture', value: boolean) => {
    onChange({ features: { ...(config.features ?? {}), [feature]: value } })
  }

  const handleNestedChange = (parentField: keyof PlatformConfig, childField: string, value: any) => {
    const currentValue = config[parentField] as any
    onChange({
      [parentField]: {
        ...currentValue,
        [childField]: value,
      },
    })
  }

  return (
    <Box sx={environmentConfigSectionRootSx(theme)}>
      {/* Current Branding Info */}
      {branding && !brandingLoading && (
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ 
            mb: 3, 
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            boxShadow: theme.shadows[2],
            color: 'text.primary',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            '& .MuiAlert-icon': {
              color: 'primary.main',
            }
          }}
        >
          <Typography 
            variant="subtitle2" 
            sx={{ 
              mb: 1, 
              color: 'text.primary',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              fontWeight: 700
            }}
          >
            Current Platform Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Chip 
              label={`Name: ${branding.platformName}`} 
              size="small" 
              variant="outlined"
              sx={{ 
                borderColor: 'divider', 
                color: 'text.primary',
                bgcolor: alpha(theme.palette.text.primary, 0.04),
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                fontWeight: 500,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                }
              }}
            />
            <Chip 
              label={`URL: ${branding.platformUrl}`} 
              size="small" 
              variant="outlined"
              sx={{ 
                borderColor: 'divider', 
                color: 'text.primary',
                bgcolor: alpha(theme.palette.text.primary, 0.04),
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                fontWeight: 500,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                }
              }}
            />
            <Chip 
              label={`Primary Color: ${branding.primaryColor}`} 
              size="small" 
              variant="outlined"
              sx={{ 
                borderColor: branding.primaryColor, 
                color: 'text.primary',
                bgcolor: alpha(branding.primaryColor, 0.12),
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                fontWeight: 500,
                '&:hover': {
                  borderColor: branding.primaryColor,
                  bgcolor: alpha(branding.primaryColor, 0.2),
                }
              }}
            />
          </Box>
        </Alert>
      )}

      {/* Contact Information */}
      <ConfigSection
        title="Platform Features"
        description="Turn features on or off for everyone in this workspace"
        icon={<TuneIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ConfigToggle
              label="Picture-in-picture"
              value={config.features?.pictureInPicture === true}
              onChange={(value) => handleFeatureChange('pictureInPicture', value)}
              description="Let people pop the call out into a floating window that stays on top of other tabs"
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      <ConfigSection
        title="Contact Information"
        description="Configure contact details and support information"
        icon={<ContactMailIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Contact Email"
              value={config.contact.email}
              onChange={(value) => handleNestedChange('contact', 'email', value)}
              type="email"
              placeholder="contact@yourplatform.com"
              disabled={disabled}
              helpText="Primary contact email address"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Phone Number"
              value={config.contact.phone || ''}
              onChange={(value) => handleNestedChange('contact', 'phone', value)}
              type="tel"
              placeholder="+1 (555) 123-4567"
              disabled={disabled}
              helpText="Contact phone number (optional)"
            />
          </Grid>
          
          <Grid item xs={12}>
            <ConfigField
              label="Address"
              value={config.contact.address || ''}
              onChange={(value) => handleNestedChange('contact', 'address', value)}
              type="text"
              placeholder="123 Business St, City, State 12345"
              disabled={disabled}
              helpText="Business address (optional)"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Support URL"
              value={config.contact.supportUrl || ''}
              onChange={(value) => handleNestedChange('contact', 'supportUrl', value)}
              type="url"
              placeholder="https://support.yourplatform.com"
              disabled={disabled}
              helpText="URL to your support portal (optional)"
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Social Media */}
      <ConfigSection
        title="Social Media"
        description="Configure social media links"
        icon={<ShareIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Twitter URL"
              value={config.social.twitter || ''}
              onChange={(value) => handleNestedChange('social', 'twitter', value)}
              type="url"
              placeholder="https://twitter.com/yourplatform"
              disabled={disabled}
              helpText="Twitter profile URL (optional)"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="LinkedIn URL"
              value={config.social.linkedin || ''}
              onChange={(value) => handleNestedChange('social', 'linkedin', value)}
              type="url"
              placeholder="https://linkedin.com/company/yourplatform"
              disabled={disabled}
              helpText="LinkedIn company page URL (optional)"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="GitHub URL"
              value={config.social.github || ''}
              onChange={(value) => handleNestedChange('social', 'github', value)}
              type="url"
              placeholder="https://github.com/yourplatform"
              disabled={disabled}
              helpText="GitHub repository URL (optional)"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Facebook URL"
              value={config.social.facebook || ''}
              onChange={(value) => handleNestedChange('social', 'facebook', value)}
              type="url"
              placeholder="https://facebook.com/yourplatform"
              disabled={disabled}
              helpText="Facebook page URL (optional)"
            />
          </Grid>
        </Grid>
      </ConfigSection>
    </Box>
  )
}

export default PlatformSettings 